import React, { FC, useState, ReactNode, ReactElement, useEffect } from 'react';
import { ActionDotsIcon } from 'src/components/SkladonIcon/SmallIcons';
import MuiButton from '@mui/material/Button';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '../Typography';
import StyledLink from '../StyledLink';
import Tooltip from '../Tooltip';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import { ButtonVariantType } from '../Button';
import Divider from '@mui/material/Divider';
import translate from '@translate';

const ItemText = styled('div')({
    display: 'inherit',
    cursor: 'pointer',
    color: colors.text_T2,

    '&:hover': {
        color: colors.text_T1,
    },

    '& p': {
        lineHeight: sizes.icon_button_FS,
    },

    '& svg': {
        width: sizes.icon_button_FS,
        height: sizes.icon_button_FS,
        color: colors.icon,
        fill: colors.icon,
        '& path': {
            fill: colors.icon,
        },
    },
});

export interface ActionType {
    icon?: ReactNode;
    label?: string;
    tooltip?: string;
    onClick?: () => void;
    getLink?: () => string;
    hiddenFce?: () => boolean;
    style?: React.CSSProperties;
    node?: ReactElement;
    divider?: boolean;
}

export const getActionElement = (
    actionItem: ActionType,
    handleClickItem: (actionItem: ActionType) => void,
    multiple?: boolean
) => {
    if (actionItem.node) {
        return actionItem.node;
    }

    if (actionItem.divider) {
        return <Divider orientation="horizontal" />;
    }

    const text = (
        <ItemText>
            {actionItem.icon && (multiple ? <ListItemIcon>{actionItem.icon}</ListItemIcon> : actionItem.icon)}
            {multiple && actionItem.label && <Typography>{actionItem.label}</Typography>}
        </ItemText>
    );

    const tooltipTitle = actionItem.tooltip || actionItem.label;
    const textTooltip = tooltipTitle ? <Tooltip title={tooltipTitle}>{text}</Tooltip> : text;

    const textElemenet = multiple ? (
        <MuiMenuItem onClick={() => handleClickItem(actionItem)}>{textTooltip}</MuiMenuItem>
    ) : (
        <MuiButton onClick={() => handleClickItem(actionItem)} style={actionItem.style}>
            {textTooltip}
        </MuiButton>
    );

    if (actionItem.getLink) {
        return <StyledLink to={actionItem.getLink?.() || ''}>{textElemenet}</StyledLink>;
    }

    return textElemenet;
};

export interface ActionsMenuPropsType {
    actions?: ActionType[];
    buttonVariant?: ButtonVariantType;
    onPopupToggle?: (value: boolean) => void;
}

const ActionsMenu: FC<ActionsMenuPropsType> = (props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        props.onPopupToggle?.(open);
    }, [open]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClickItem = (actionItem: ActionType) => {
        if (actionItem.onClick) {
            actionItem.onClick();
        }
        handleClose();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title={translate('actionItem.moreActions')}>
                <MuiButton
                    id="table-actions-button"
                    aria-controls={open ? 'table-actions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    variant={props.buttonVariant}
                >
                    <ActionDotsIcon />
                </MuiButton>
            </Tooltip>
            {open && (
                <MuiMenu
                    id="table-actions-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'table-actions-button',
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                >
                    {props.actions?.map((actionItem, index) => (
                        <div key={index}>{getActionElement(actionItem, handleClickItem, true)}</div>
                    ))}
                </MuiMenu>
            )}
        </>
    );
};

export default ActionsMenu;
